import { useEffect, useSyncExternalStore } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useLocation } from 'react-router-dom'
import {
  buildUserProperties,
  captureAttribution,
  getConsent,
  getVersion,
  initAnalytics,
  isBannerVisible,
  isConfigured,
  isExcludedPath,
  reopenBanner,
  setConsent,
  setUserProperties,
  subscribe,
  trackPageView,
} from '../lib/analytics'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import type { OrganizationDetail } from '../lib/types'
import { Button } from './ui/Button'

function useAnalyticsState() {
  useSyncExternalStore(subscribe, getVersion, () => 0)
}

/**
 * Loads the public analytics config, sends SPA page views, and shows the
 * cookie banner. Renders nothing unless GA is configured on the server and
 * the visitor has not chosen yet (or asked to change the choice). Never
 * shows on excluded (debtor/token) routes.
 */
export function AnalyticsGate() {
  const { pathname } = useLocation()
  useAnalyticsState()
  const { user, isImpersonating } = useAuth()

  useEffect(() => {
    captureAttribution() // first touch: must run before anything navigates away
    void initAnalytics()
  }, [])

  // User properties (enums/buckets only) once consented and logged in.
  // Support impersonation and platform admins are never measured.
  const measured = getConsent() === 'granted' && !!user && !isImpersonating && !user.is_system_admin
  const org = useQuery({
    queryKey: ['organization'],
    queryFn: () => api.get<OrganizationDetail>('/api/v1/organizations/me'),
    enabled: measured,
  })
  const version = getVersion()
  useEffect(() => {
    if (measured) setUserProperties(buildUserProperties(user, org.data ?? null))
  }, [measured, user, org.data, version])

  useEffect(() => {
    // Let react-helmet-async update document.title first.
    const t = window.setTimeout(trackPageView, 200)
    return () => window.clearTimeout(t)
  }, [pathname])

  if (isExcludedPath(pathname) || !isBannerVisible()) return null

  return (
    <section
      aria-labelledby="consent-title"
      data-testid="consent-banner"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background p-4 shadow-lg"
    >
      <div className="mx-auto flex max-w-[1120px] flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-foreground">
          <h2 id="consent-title" className="font-semibold">
            Can we count visits?
          </h2>
          <p className="mt-1">
            We use Google Analytics to see which pages are used. It records page views and button
            clicks, never your name, email, phone number or customer details. It stays off unless
            you accept.{' '}
            <Link to="/privacy#analytics-cookies" className="underline underline-offset-2">
              Privacy policy
            </Link>
          </p>
        </div>
        <div className="flex shrink-0 gap-3">
          <Button variant="secondary" className="min-h-11 min-w-28 flex-1" onClick={() => setConsent('granted')}>
            Accept
          </Button>
          <Button variant="secondary" className="min-h-11 min-w-28 flex-1" onClick={() => setConsent('denied')}>
            No thanks
          </Button>
        </div>
      </div>
    </section>
  )
}

/** Footer link to change the choice. Hidden when analytics is not configured. */
export function CookieSettingsLink({ className }: { className?: string }) {
  useAnalyticsState()
  if (!isConfigured()) return null
  return (
    <button type="button" className={className} onClick={reopenBanner}>
      Cookie settings
    </button>
  )
}
